import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Activity, AlertCircle, CheckCircle2, DollarSign, Loader2, Zap } from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import {
  fetchRecentLlmCalls,
  fetchLlmSummaryByTenant,
  fetchLlmSummaryByFunction,
} from "@/platform/billing/llm-calls-api";

export function AdminLlmCallsPage() {
  const recentQuery = useQuery({
    queryKey: ["admin", "llm", "recent"],
    queryFn: () => fetchRecentLlmCalls(50),
    refetchInterval: 30_000,
  });

  const byTenantQuery = useQuery({
    queryKey: ["admin", "llm", "by-tenant"],
    queryFn: fetchLlmSummaryByTenant,
    refetchInterval: 60_000,
  });

  const byFunctionQuery = useQuery({
    queryKey: ["admin", "llm", "by-function"],
    queryFn: fetchLlmSummaryByFunction,
    refetchInterval: 60_000,
  });

  const totalCost = (byTenantQuery.data ?? []).reduce((s, t) => s + t.cost_brl, 0);
  const totalCalls = (byTenantQuery.data ?? []).reduce((s, t) => s + t.calls, 0);
  const totalErrors = (byTenantQuery.data ?? []).reduce((s, t) => s + t.errors, 0);

  return (
    <Container size="wide" className="py-12 md:py-16">
      <header className="mb-10">
        <Badge variant="accentSolid" className="gap-2">
          <Activity className="size-3" aria-hidden />
          Admin · LLM Calls
        </Badge>
        <h1 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
          Telemetria de chamadas LLM
        </h1>
        <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
          Custo, latência e taxa de erro das últimas 1.000 chamadas. Atualiza a cada 60s.
        </p>
      </header>

      {/* KPIs globais */}
      <div className="grid gap-4 md:grid-cols-3 mb-10">
        <Kpi
          icon={DollarSign}
          label="Custo total (últimas 1.000)"
          value={`R$ ${totalCost.toFixed(2).replace(".", ",")}`}
          tone="green"
        />
        <Kpi
          icon={Zap}
          label="Chamadas"
          value={totalCalls.toLocaleString("pt-BR")}
          tone="violet"
        />
        <Kpi
          icon={AlertCircle}
          label="Erros"
          value={totalErrors.toString()}
          tone={totalErrors > 0 ? "red" : "green"}
        />
      </div>

      {/* Por tenant */}
      <section className="mb-10">
        <h2 className="mb-4 font-display text-[length:var(--text-body-lg)] font-semibold">
          Por tenant
        </h2>
        {byTenantQuery.isLoading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[--color-border]">
            <table className="w-full text-[length:var(--text-body-sm)]">
              <thead className="bg-[color:var(--color-muted)] text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Tenant</th>
                  <th className="px-4 py-3 text-right font-medium">Calls</th>
                  <th className="px-4 py-3 text-right font-medium">Custo (R$)</th>
                  <th className="px-4 py-3 text-right font-medium">Lat média</th>
                  <th className="px-4 py-3 text-right font-medium">Erros</th>
                </tr>
              </thead>
              <tbody>
                {byTenantQuery.data?.map((t) => (
                  <tr key={t.tenant} className="border-t border-[--color-border]">
                    <td className="px-4 py-3 font-medium">{t.tenant}</td>
                    <td data-tabular="true" className="px-4 py-3 text-right">
                      {t.calls.toLocaleString("pt-BR")}
                    </td>
                    <td data-tabular="true" className="px-4 py-3 text-right font-medium">
                      {t.cost_brl.toFixed(2).replace(".", ",")}
                    </td>
                    <td
                      data-tabular="true"
                      className="px-4 py-3 text-right text-[color:var(--color-muted-foreground)]"
                    >
                      {(t.avg_latency_ms / 1000).toFixed(1)}s
                    </td>
                    <td
                      data-tabular="true"
                      className={`px-4 py-3 text-right ${t.errors > 0 ? "font-semibold" : "text-[color:var(--color-muted-foreground)]"}`}
                      style={{ color: t.errors > 0 ? "hsl(0 65% 50%)" : undefined }}
                    >
                      {t.errors}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Por função */}
      <section className="mb-10">
        <h2 className="mb-4 font-display text-[length:var(--text-body-lg)] font-semibold">
          Top funções (por custo)
        </h2>
        {byFunctionQuery.isLoading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[--color-border]">
            <table className="w-full text-[length:var(--text-body-sm)]">
              <thead className="bg-[color:var(--color-muted)] text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Função</th>
                  <th className="px-4 py-3 text-right font-medium">Calls</th>
                  <th className="px-4 py-3 text-right font-medium">Custo (R$)</th>
                  <th className="px-4 py-3 text-right font-medium">Custo/call</th>
                  <th className="px-4 py-3 text-right font-medium">Lat média</th>
                </tr>
              </thead>
              <tbody>
                {byFunctionQuery.data?.map((f) => (
                  <tr key={f.function_name} className="border-t border-[--color-border]">
                    <td className="px-4 py-3 font-mono text-[color:var(--color-foreground)]">
                      {f.function_name}
                    </td>
                    <td data-tabular="true" className="px-4 py-3 text-right">
                      {f.calls}
                    </td>
                    <td data-tabular="true" className="px-4 py-3 text-right font-medium">
                      {f.cost_brl.toFixed(2).replace(".", ",")}
                    </td>
                    <td
                      data-tabular="true"
                      className="px-4 py-3 text-right text-[color:var(--color-muted-foreground)]"
                    >
                      {(f.cost_brl / f.calls).toFixed(3).replace(".", ",")}
                    </td>
                    <td
                      data-tabular="true"
                      className="px-4 py-3 text-right text-[color:var(--color-muted-foreground)]"
                    >
                      {(f.avg_latency_ms / 1000).toFixed(1)}s
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Últimas chamadas */}
      <section>
        <h2 className="mb-4 font-display text-[length:var(--text-body-lg)] font-semibold">
          Últimas 50 chamadas
        </h2>
        {recentQuery.isLoading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[--color-border]">
            <table className="w-full text-[length:var(--text-body-sm)]">
              <thead className="bg-[color:var(--color-muted)] text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Quando</th>
                  <th className="px-4 py-3 text-left font-medium">Tenant</th>
                  <th className="px-4 py-3 text-left font-medium">Função</th>
                  <th className="px-4 py-3 text-left font-medium">Model</th>
                  <th className="px-4 py-3 text-right font-medium">In / Out</th>
                  <th className="px-4 py-3 text-right font-medium">R$</th>
                  <th className="px-4 py-3 text-right font-medium">Lat</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentQuery.data?.map((c) => (
                  <tr key={c.id} className="border-t border-[--color-border]">
                    <td className="px-4 py-3 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                      {c.created_at
                        ? format(parseISO(c.created_at), "d/MMM HH:mm:ss", { locale: ptBR })
                        : "—"}
                    </td>
                    <td className="px-4 py-3">{c.tenant ?? "—"}</td>
                    <td className="px-4 py-3 font-mono">{c.function_name}</td>
                    <td className="px-4 py-3 font-mono text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                      {c.model}
                    </td>
                    <td
                      data-tabular="true"
                      className="px-4 py-3 text-right text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]"
                    >
                      {(c.prompt_tokens ?? 0).toLocaleString("pt-BR")}/{(c.completion_tokens ?? 0).toLocaleString("pt-BR")}
                    </td>
                    <td data-tabular="true" className="px-4 py-3 text-right font-medium">
                      {Number((c.cost_usd ?? 0) * 5).toFixed(3).replace(".", ",")}
                    </td>
                    <td
                      data-tabular="true"
                      className="px-4 py-3 text-right text-[color:var(--color-muted-foreground)]"
                    >
                      {c.latency_ms ? `${(c.latency_ms / 1000).toFixed(1)}s` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {c.status === "ok" ? (
                        <CheckCircle2
                          className="size-4"
                          style={{ color: "hsl(155 60% 38%)" }}
                          aria-hidden
                        />
                      ) : (
                        <AlertCircle
                          className="size-4"
                          style={{ color: "hsl(0 65% 50%)" }}
                          aria-hidden
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Container>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  tone: "violet" | "green" | "red";
}) {
  const toneStyles = {
    violet: { bg: "hsl(255 78% 96%)", fg: "hsl(255 75% 45%)" },
    green: { bg: "hsl(155 50% 95%)", fg: "hsl(155 60% 38%)" },
    red: { bg: "hsl(0 50% 95%)", fg: "hsl(0 65% 50%)" },
  }[tone];

  return (
    <article className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6 shadow-[var(--shadow-sm)]">
      <span
        className="chip size-9"
        style={{ backgroundColor: toneStyles.bg, color: toneStyles.fg }}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <p className="mt-4 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </p>
      <p
        data-tabular="true"
        className="mt-1 font-display text-[length:var(--text-display-sm)] font-semibold leading-tight"
        style={{ color: toneStyles.fg }}
      >
        {value}
      </p>
    </article>
  );
}
